import { DocumentRequirementApi } from '@rightship-tech/document-management-app/apis/document-requirement.api';
import { DocumentManagementRoutes } from '@rightship-tech/document-management-app/constants/route.constant';
import { autoinject } from "aurelia-framework";
import { Router } from 'aurelia-router';
import { UserClientService } from '@rightship-tech/common-ui/services/basic/user-client.service';
import { MyVesselService } from '../../../services/my-vessel.service';
import { MyTasksService } from '../../../services/my-tasks.service';
import { GetTasksRequest } from '@rightship-tech/tasks-ui/models/tasks.models';
import { LookupValue } from "@rightship-tech/common-ui/models/lookup.models";
import { DialogClientService } from '@rightship-tech/common-ui/services/basic/dialog-client.service';
import { myVesselsDialogConstants } from '../../../constants/dialog.constants';
import { MyVesselsRouteConstants } from '../../../constants/route.constants';
import { CacheClientService, CacheLocation } from '@rightship-tech/common-ui/services/basic/client-caching.service';
import { EncodingHelper } from "@rightship-tech/common-ui/helpers/encoding.helper";
import { UserClaimsService } from '@rightship-tech/common-ui/services/basic/user-claims.service';
import { Claims } from '../../../constants/claims.constants';

@autoinject
export class Dashboard {

	public currentUserFullName: string;
	public pendingTasksCount: number = 0;
	public pendingTasksLoading: boolean = true;
	public vesselsInMyFleetCount: number = 0;
	public vesselsInMyFleetLoading: boolean = true;
	public documentRequirementsCount: number = 0;
	public documentRequirementsLoading: boolean = true;
	public showDocumentRequirements: boolean = false;
	public vessels: LookupValue[] = [];
	public vesselIds: number[] = [];
	public selectedFilters: string[] = [];

	constructor(private router: Router, private userClientService: UserClientService,
		private myTaskService: MyTasksService, private dialogClientService: DialogClientService,
		private myVesselService: MyVesselService, private cacheClientService: CacheClientService, 
		private documentRequirementApi: DocumentRequirementApi, private userClaimsService: UserClaimsService) {
			this.showDocumentRequirements = this.userClaimsService.hasPermission(Claims.ShowDocumentRequirementInHome)
	}

	async activate() {

		var vesselIds = await this.cacheClientService.get<number[]>("filter-vessel-ids", CacheLocation.SessionStorage, false);
		this.vesselIds = vesselIds? vesselIds: [];
		
		const urlParams = new URLSearchParams(window.location.search);

		if (urlParams.get("verifyMouData") && urlParams.get("verifyMouData") != "") {
			//Set MOU Data Verification filter to true
			this.selectedFilters.push('mouDataVerification');
		}
		
		if (urlParams.get("env-reviews") && urlParams.get("env-reviews") != "") 
		{
			//Set environmental reviews filter to true
			this.selectedFilters.push('env');
		}
		
		// done as part of https://rightship.atlassian.net/browse/ET1-686
		if (urlParams.get("doc-req") && urlParams.get("doc-req") != "") 
		{
			//Set document requirement filter to true
			this.selectedFilters.push('documentRequirements');
		}
	}

	async attached() {

		//Get the current user's full name
		this.currentUserFullName = this.userClientService.currentUser?.user.fullName;
		
		//Get My Vessel count for widget
		await this.initializeWidgets();
		
		//Open environmental reviews dialog
		await this.openEnvReviewsDialog();
	}

	async initializeWidgets() {

		let request: GetTasksRequest = {
			vessels: this.vessels?.map((vessel) => vessel.id),
			statuses: [],
			rowPerPage: 10,
			orderBy: "ASC",
			sortBy: "Date"
		};

		//Get Pending Task count for widget
		this.pendingTasksCount = await this.myTaskService.getPendingTaskCount(request);
		this.pendingTasksLoading = false;

		//Get vessels in my fleet count
		this.vesselsInMyFleetCount = await this.myVesselService.countMyVessel();
		var response = await this.myVesselService.getMyVessels();

		//Get vessels lookup for the filter
		this.vessels = response?.result.map((vessel) => {
			return {
				id: vessel.vesselId,
				name: vessel.identifier + "    " + vessel.vesselName
			};
		});
		this.vesselsInMyFleetLoading = false;

		//Get document requirements
		this.documentRequirementsCount = await this.documentRequirementApi.getSubscribedDocumentCount();
		this.documentRequirementsLoading = false;
	}

	//Navigate to all tasks
	public navigateToAllTasks() {
		this.router.navigateToRoute(MyVesselsRouteConstants.AllTasks);
	}

	//Open My Monitored Vessels dialog
	public openMyMonitoredVesselsDialog() {
		this.dialogClientService.show(myVesselsDialogConstants.myMonitoredVesselsDialog, {
			onVesselChanges: (() => {
				this.initializeWidgets();
			}).bind(this)
		}, true, false, false, "right");
	}

	//Navigate to Document Requirements page
	public navigateToDocumentRequirementsPage() {
		this.router.navigateToRoute(DocumentManagementRoutes.ManageDocumentRequirements);
	}

	//Open environment reviews dialog
	async openEnvReviewsDialog() {		
		
		//Environmnetal reviews query string - ET1-334
        let environmentalReviews;
		const urlParams = new URLSearchParams(window.location.search);

		if (urlParams.get("env-reviews") && urlParams.get("env-reviews") != "") {
			environmentalReviews = EncodingHelper.decodeUrl(urlParams.get("env-reviews")) as unknown as boolean;
			let emailIdentifier = EncodingHelper.decodeUrl(urlParams.get("eg")) as string;

			//Get the dialog-environmental-review-closed from cache
			const itWasClosedAlready = await this.cacheClientService.get(`dialog-environmental-review-closed-${emailIdentifier}`, CacheLocation.LocalStorage, false);

			//If environmental reviews is passed as true, set the environmental reviews filter to true and show dialog
			if (environmentalReviews && !itWasClosedAlready) {
				
				//Show dialog
				this.dialogClientService.show(myVesselsDialogConstants.addVesselsForEnviromnetalReviewDialog, {
					emailIdentifier: emailIdentifier,
					onVesselChanges: (() => {
						this.initializeWidgets();
					}).bind(this)
				}, true);
			}
		}
	}

	//Time of day - Gets good morning, afternoon or evening based on the time of the day
	public timeOfDay() {
		let time = new Date().getHours();
		if (time < 12) {
			return "Good Morning";
		} else if (time < 18) {
			return "Good Afternoon";
		} else {
			return "Good Evening";
		}
	}
}